import { useMutation } from '@apollo/client';
import { ClassificationType } from '@classification-testing/domain';
import type { ClassificationContent, ClassificationResult, ContentType } from '@classification-testing/domain';
import { CLASSIFY_CONTENT } from '@classification-testing/graphql';
import { addClassificationResult, setClassificationError, setClassificationLoading } from '@classification-testing/state/actions';
import { selectCommunityResultHistory, selectClassificationLoading, selectClassificationError } from '@classification-testing/state/selectors';
import { transformErrors } from '@core/adapters/errors';
import { track } from '@core/logging';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useAppDispatch, useAppSelector } from '@store';
import { useCallback } from 'react';

// GraphQL types for direct classification (BFF converted format)
interface ClassifyContentResponse {
  text: string;
  elapsed: number;
  contentType: string;
  classificationType: string;
  confidenceScore: number;
  explanation?: string;
  contentUrl?: string;
  topicName?: string;
  labels: Array<{
    severity: number;
    label_num_map: number;
    label_name: string;
    label_short_name: string;
    score?: number;
  }>;
  language?: string;
  languageDetected?: string;
  timestamp: string;
  policies?: Array<{
    id: string;
    type: string;
    created_by: string;
    created_at: string;
    last_modified_by: string;
    last_modified_at: string;
    community_id: string;
    is_enabled: boolean;
    name: string;
    description: string;
    labels: Array<{
      label_id: string;
      severity: number;
    }>;
  }>;
  non_triggered_policies?: Array<{
    id: string;
    type: string;
    created_by: string;
    created_at: string;
    last_modified_by: string;
    last_modified_at: string;
    community_id: string;
    is_enabled: boolean;
    name: string;
    description: string;
    labels: Array<{
      label_id: string;
      severity: number;
    }>;
  }>;
}

interface ClassifyContentMutationResponse {
  classifyContent: ClassifyContentResponse;
}

interface ClassifyContentVariables {
  input: {
    communityId?: string;
    promptVersion?: string;
    text?: string;
    imageName?: string;
    audioName?: string;
    username?: string;
  };
}

/**
 * Extract display text based on content type
 */
function getDisplayText(content: ClassificationContent, response: ClassifyContentResponse): string {
  if (content.type === 'text') {
    return content.text || response.text || '';
  }

  if (content.type === 'username') {
    return content.username || response.text || '';
  }

  return response.text || '';
}

/**
 * Map direct classification GraphQL response to domain model
 */
function mapDirectClassificationResult(content: ClassificationContent, response: ClassifyContentResponse, _communityId: string) {
  const displayText = getDisplayText(content, response);
  // Use content type from response or infer from content
  const contentType = (response.contentType as ContentType) || (content.type as ContentType);
  const classificationType = (response.classificationType as ClassificationType) || ClassificationType.SAFE_CONTENT;
  // Use the labels from the BFF which includes all label properties
  const labels = response.labels || [];

  const result: ClassificationResult = {
    id: Date.now().toString(),
    contentType,
    classificationType,
    labels,
    timestamp: response.timestamp,
    confidenceScore: response.confidenceScore,
    language: response.language,
    languageDetected: response.languageDetected,
    languageClassified: response.language, // Language used for classification
    text: displayText,
    explanation: response.explanation,
    contentUrl: response.contentUrl,
    topicName: response.topicName,
    policies: response.policies,
    nonTriggeredPolicies: response.non_triggered_policies,
    duration: response.elapsed,
  };

  return result;
}

export function useClassifyWithoutUpload(communityId?: string, promptVersion?: string, topicName?: string | null) {
  const dispatch = useAppDispatch();

  // Redux state selectors
  const reduxLoading = useAppSelector(selectClassificationLoading);
  const reduxError = useAppSelector(selectClassificationError);
  const resultHistory = useAppSelector(communityId ? selectCommunityResultHistory(communityId) : () => []);

  // Direct classification GraphQL mutation
  const [classifyMutation, { loading: mutationLoading }] = useMutation<ClassifyContentMutationResponse, ClassifyContentVariables>(CLASSIFY_CONTENT, {
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  // Direct content classification function
  const classifyContent = useCallback(
    // eslint-disable-next-line max-statements, complexity
    async (content: ClassificationContent) => {
      // Clear previous errors and set loading state
      dispatch(setClassificationError(null));
      dispatch(setClassificationLoading(true));

      try {
        track('ClassifyContentWithoutUploadInitiated', {
          communityId: communityId || 'unknown',
          contentType: content.type,
          promptVersion: promptVersion || 'default',
        });

        const result = await classifyMutation({
          variables: {
            input: {
              communityId,
              ...(promptVersion && { promptVersion }),
              ...(topicName && { topicName }),
              ...(content.type === 'text' && { text: content.text }),
              ...(content.type === 'image' && { imageName: content.imageName }),
              ...(content.type === 'audio' && { audioName: content.audioName }),
              ...(content.type === 'username' && { username: content.username }),
            },
          },
        });

        if (result.errors && result.errors.length > 0) {
          const errors = transformErrors(result.errors[0]);

          dispatch(setClassificationError(errors));
          throw errors;
        }

        if (!result.data?.classifyContent) {
          const errors = transformErrors(new Error('No data returned from GraphQL mutation'));

          dispatch(setClassificationError(errors));
          throw errors;
        }

        // Map to domain model and add to Redux state
        const classificationResult = mapDirectClassificationResult(content, result.data.classifyContent, communityId || '');

        // Only dispatch to Redux if we have a valid communityId
        if (communityId) {
          dispatch(addClassificationResult({ communityId, result: classificationResult }));
        }

        dispatch(setClassificationLoading(false));

        track('ClassifyContentWithoutUploadSucceeded', {
          communityId: communityId || 'unknown',
          contentType: content.type,
          promptVersion: promptVersion || 'default',
        });

        return classificationResult;
      } catch (error) {
        track('ClassifyContentWithoutUploadFailed', {
          communityId: communityId || 'unknown',
          contentType: content.type,
          promptVersion: promptVersion || 'default',
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        const errors = transformErrors(error);

        dispatch(setClassificationError(errors));
        dispatch(setClassificationLoading(false));
        throw errors;
      }
    },

    [classifyMutation, communityId, dispatch, promptVersion, topicName]
  );

  return {
    classifyContent,
    resultHistory,
    isLoading: mutationLoading || reduxLoading,
    errors: reduxError,
  };
}
